import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { showSuccess, showError, showConfirmDanger } from '../services/swal';

const Suppliers = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        address: ''
    });
    const [editingId, setEditingId] = useState(null);

    const fetchSuppliers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/suppliers', { params: { search } });
            setSuppliers(res.data.data);
        } catch (error) {
            console.error("Failed to fetch suppliers", error);
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            if (editingId) {
                await api.put(`/suppliers/${editingId}`, formData);
                showSuccess('Updated!', 'Supplier details have been updated.');
            } else {
                await api.post('/suppliers', formData);
                showSuccess('Added!', 'New supplier has been registered.');
            }
            setShowModal(false);
            setFormData({ name: '', contact_person: '', email: '', phone: '', address: '' });
            setEditingId(null);
            fetchSuppliers();
        } catch (error) {
            showError('Save Failed', 'Failed to save supplier details.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        const result = await showConfirmDanger('Remove Supplier?', 'This will permanently remove the supplier from the command center.');
        if (result.isConfirmed) {
            try {
                await api.delete(`/suppliers/${id}`);
                fetchSuppliers();
                showSuccess('Removed!', 'Supplier has been purged.');
            } catch (error) {
                showError('Delete Failed', 'Failed to remove supplier.');
            }
        }
    };

    const handleEdit = (supplier) => {
        setFormData({
            name: supplier.name,
            contact_person: supplier.contact_person || '',
            email: supplier.email || '',
            phone: supplier.phone || '',
            address: supplier.address || ''
        });
        setEditingId(supplier.id);
        setShowModal(true);
    };

    return (
        <Layout title="Vendor Intelligence Terminal">
            <div className="suppliers-workspace animate__animated animate__fadeIn">
                
                {/* Analytics Overview */}
                <div className="row g-4 mb-4">
                    <div className="col-12 col-sm-6 col-xl-3">
                        <div className="stats-card-premium p-4 rounded-4 shadow-sm border-0 h-100">
                            <div className="d-flex align-items-center gap-3">
                                <div className="stats-icon-box bg-primary bg-opacity-10 text-primary"><i className="bi bi-building-fill fs-4"></i></div>
                                <div>
                                    <div className="text-muted small fw-bold text-uppercase ls-1">Total Vendors</div>
                                    <h3 className="fw-extrabold mb-0">{suppliers.length}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-3">
                        <div className="stats-card-premium p-4 rounded-4 shadow-sm border-0 h-100 text-white" style={{background: 'linear-gradient(135deg, #6c5dd3 0%, #a098ed 100%)'}}>
                            <div className="d-flex align-items-center gap-3">
                                <div className="stats-icon-box bg-white bg-opacity-20"><i className="bi bi-shield-check fs-4" style={{color: 'white !important'}}></i></div>
                                <div>
                                    <div className="text-white text-opacity-75 small fw-bold text-uppercase ls-1">Active Accounts</div>
                                    <h3 className="fw-extrabold mb-0">{suppliers.length}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-3">
                        <div className="stats-card-premium p-4 rounded-4 shadow-sm border-0 h-100">
                            <div className="d-flex align-items-center gap-3">
                                <div className="stats-icon-box bg-success bg-opacity-10 text-success"><i className="bi bi-truck fs-4"></i></div>
                                <div>
                                    <div className="text-muted small fw-bold text-uppercase ls-1">Recent Deliveries</div>
                                    <h3 className="fw-extrabold mb-0">12</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-sm-6 col-xl-3">
                        <div className="stats-card-premium p-4 rounded-4 shadow-sm border-0 h-100">
                            <div className="d-flex align-items-center gap-3">
                                <div className="stats-icon-box bg-warning bg-opacity-10 text-warning"><i className="bi bi-wallet2 fs-4"></i></div>
                                <div>
                                    <div className="text-muted small fw-bold text-uppercase ls-1">Outstanding</div>
                                    <h3 className="fw-extrabold mb-0">0</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="card-header bg-transparent p-4 border-0">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                            <div>
                                <h5 className="fw-bold mb-0">Supplier Registry</h5>
                                <small className="text-muted">Manage your B2B supply chain and procurement contacts</small>
                            </div>
                            <div className="d-flex gap-2">
                                <div className="modern-search-box flex-grow-1">
                                    <i className="bi bi-search"></i>
                                    <input 
                                        type="text" 
                                        placeholder="Search by name, email..." 
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                                <button className="btn btn-primary rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-2" onClick={() => { setEditingId(null); setFormData({ name: '', contact_person: '', email: '', phone: '', address: '' }); setShowModal(true); }}>
                                    <i className="bi bi-plus-lg"></i> <span className="d-none d-lg-inline">Add Supplier</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 custom-modern-table">
                                <thead>
                                    <tr>
                                        <th className="ps-4">Supplier Identity</th>
                                        <th>Contact Executive</th>
                                        <th>Communication Hub</th>
                                        <th>Geographic Address</th>
                                        <th className="text-end pe-4">Operations</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-primary"></div></td></tr>
                                    ) : suppliers.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-5">
                                                <div className="py-4">
                                                    <i className="bi bi-inbox fs-1 text-muted opacity-25"></i>
                                                    <p className="text-muted mt-2">No vendor records found in the database.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        suppliers.map(supplier => (
                                            <tr key={supplier.id}>
                                                <td className="ps-4">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="vendor-avatar bg-light border text-primary fw-bold rounded-3 d-flex align-items-center justify-content-center" style={{width:'40px', height:'40px'}}>
                                                            {supplier.name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold mb-0">{supplier.name}</div>
                                                            <span className="badge bg-success bg-opacity-10 text-success rounded-pill x-small px-2">Active Vendor</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="small fw-bold">{supplier.contact_person || 'N/A'}</div>
                                                    <small className="text-muted text-uppercase x-small ls-1">Procurement Lead</small>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column gap-1">
                                                        {supplier.email && <div className="small"><i className="bi bi-envelope-at me-2 text-primary opacity-75"></i>{supplier.email}</div>}
                                                        {supplier.phone && <div className="small"><i className="bi bi-phone me-2 text-primary opacity-75"></i>{supplier.phone}</div>}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="text-muted small text-truncate" style={{maxWidth: '200px'}} title={supplier.address}>
                                                        <i className="bi bi-geo-alt me-1"></i> {supplier.address || 'Global/Remote'}
                                                    </div>
                                                </td>
                                                <td className="text-end pe-4">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        <button className="btn btn-icon-modern bg-primary bg-opacity-10 text-primary border-0" onClick={() => handleEdit(supplier)} title="Edit Record">
                                                            <i className="bi bi-pencil-square"></i>
                                                        </button>
                                                        <button className="btn btn-icon-modern bg-danger bg-opacity-10 text-danger border-0" onClick={() => handleDelete(supplier.id)} title="Delete Record">
                                                            <i className="bi bi-trash3-fill"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modern Modal */}
            {showModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }} onClick={() => setShowModal(false)}>
                    <div className="modal-dialog modal-dialog-centered modal-lg" onClick={e => e.stopPropagation()}>
                        <form onSubmit={handleSave} className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                            <div className="modal-header border-0 p-4 pb-0">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="stats-icon-box bg-primary bg-opacity-10 text-primary"><i className={`bi bi-${editingId ? 'pencil-square' : 'plus-circle-fill'} fs-4`}></i></div>
                                    <div>
                                        <h5 className="modal-title fw-bold">{editingId ? 'Edit Vendor Dossier' : 'Register New Vendor'}</h5>
                                        <small className="text-muted">Enter high-accuracy details for the supply chain partner</small>
                                    </div>
                                </div>
                                <button type="button" className="btn-close shadow-none" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="row g-4">
                                    <div className="col-12 col-md-6">
                                        <label className="form-label small fw-bold text-muted text-uppercase ls-1">Company Name <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control custom-input" placeholder="e.g. Acme Logistics" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label small fw-bold text-muted text-uppercase ls-1">Contact Executive</label>
                                        <input type="text" className="form-control custom-input" placeholder="Name of representative" value={formData.contact_person} onChange={e => setFormData({ ...formData, contact_person: e.target.value })} />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label small fw-bold text-muted text-uppercase ls-1">Official Email</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-end-0"><i className="bi bi-envelope text-muted"></i></span>
                                            <input type="email" className="form-control custom-input border-start-0" placeholder="support@company.com" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label small fw-bold text-muted text-uppercase ls-1">Phone Number</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-end-0"><i className="bi bi-telephone text-muted"></i></span>
                                            <input type="text" className="form-control custom-input border-start-0" placeholder="+1 (234) 567-890" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label small fw-bold text-muted text-uppercase ls-1">Physical / Logistics Address</label>
                                        <textarea className="form-control custom-input" style={{ height: '120px' }} placeholder="Full operational address..." value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })}></textarea>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer border-0 p-4 pt-0">
                                <button type="button" className="btn btn-light rounded-pill px-4 fw-bold" onClick={() => setShowModal(false)}>Discard</button>
                                <button type="submit" className="btn btn-primary rounded-pill px-5 fw-bold shadow-sm" disabled={isSaving}>
                                    {isSaving ? <><span className="spinner-border spinner-border-sm me-2"></span>Syncing...</> : <><i className="bi bi-cloud-arrow-up-fill me-2"></i>{editingId ? 'Update Record' : 'Commit Registration'}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .stats-card-premium { background: var(--card-bg); transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); border: 1px solid var(--border-color) !important; }
                .stats-card-premium:hover { transform: translateY(-5px); box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important; }
                .stats-icon-box { width: 52px; height: 52px; border-radius: 16px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
                
                .modern-search-box { position: relative; min-width: 280px; }
                .modern-search-box i { position: absolute; left: 15px; top: 50%; transform: translateY(-50%); color: var(--text-muted-color); }
                .modern-search-box input { width: 100%; height: 48px; padding: 0 15px 0 45px; border-radius: 50px; border: 1px solid var(--border-color); background: var(--bg-color); color: var(--text-color); outline: none; transition: all 0.3s ease; }
                .modern-search-box input:focus { border-color: var(--primary-color); box-shadow: 0 0 0 4px rgba(108, 93, 211, 0.1); }

                .custom-modern-table thead th { background: var(--bg-color); color: var(--text-muted-color); font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 800; border: none; padding: 18px 12px; }
                .custom-modern-table tbody td { padding: 20px 12px; border-bottom: 1px solid var(--border-color); }
                .custom-modern-table tbody tr:last-child td { border-bottom: none; }
                
                .btn-icon-modern { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; font-size: 1.1rem; }
                .btn-icon-modern:hover { transform: scale(1.1); }
                
                .custom-input { background: var(--bg-color); border: 1.5px solid var(--border-color); border-radius: 12px; padding: 12px 16px; font-weight: 500; transition: all 0.3s ease; color: var(--text-color); }
                .custom-input:focus { background: var(--card-bg); border-color: var(--primary-color); box-shadow: none; }
                .input-group-text { border: 1.5px solid var(--border-color); border-radius: 12px 0 0 12px !important; }
                .custom-input.border-start-0 { border-radius: 0 12px 12px 0 !important; }

                [data-bs-theme="dark"] .stats-card-premium { background: #1A1A2E; }
                [data-bs-theme="dark"] .card { background: #1A1A2E; }
                [data-bs-theme="dark"] .modal-content { background: #1A1A2E; border: 1px solid rgba(255,255,255,0.05); }
                [data-bs-theme="dark"] .custom-modern-table thead th { background: rgba(255,255,255,0.02); }
            `}</style>
        </Layout>
    );
};

export default Suppliers;
